<script setup lang="ts">
import { computed } from "vue";
import { replay, type Turn } from "../../domain/rules.js";
import SyncBadge from "../sync/SyncBadge.vue";
import type { SyncStatus } from "../../sync/types.js";

/**
 * Result screen, PRD FR-4.
 *
 * Winner and the turns they needed, the three awards from FR-4.2, the full
 * standings with medals for the top three, and the rematch that keeps the same
 * playing order.
 */

const props = defineProps<{
  order: string[];
  names: Record<string, string>;
  turns: Turn[];
  status: SyncStatus;
}>();

const emit = defineEmits<{ rematch: []; close: [] }>();

const state = computed(() => replay(props.order, props.turns));
const nameOf = (id: string): string => props.names[id] ?? id;

const winner = computed(() => state.value.standings[0] ?? null);

const MEDALS = ["🥇", "🥈", "🥉"];

/** FR-4.2: biggest single turn, best average per banked turn, most strikes. */
const awards = computed(() => {
  const players = state.value.players;
  const out: { label: string; who: string; value: string }[] = [];

  const best = [...players].sort((a, b) => b.best - a.best)[0];
  if (best && best.best > 0) {
    out.push({
      label: "Größter Wurf",
      who: nameOf(best.playerId),
      value: best.best.toLocaleString("de-DE"),
    });
  }

  // Average of what a player actually banked -- bankedPoints, not total, so the
  // strike penalty does not drag down someone who banked well and merely also
  // struck a few times.
  const scored = players.filter((p) => p.banked > 0);
  const steady = [...scored].sort(
    (a, b) => b.bankedPoints / b.banked - a.bankedPoints / a.banked,
  )[0];
  if (steady) {
    out.push({
      label: "Konstanz",
      who: nameOf(steady.playerId),
      value: Math.round(steady.bankedPoints / steady.banked).toLocaleString("de-DE"),
    });
  }

  const unlucky = [...players].sort((a, b) => b.struck - a.struck)[0];
  if (unlucky && unlucky.struck > 0) {
    out.push({
      label: "Pechvogel",
      who: nameOf(unlucky.playerId),
      value: `${unlucky.struck}× Strich`,
    });
  }

  return out;
});
</script>

<template>
  <div class="result">
    <div class="scroll">
      <!-- The moment people most want to know the evening is safely stored. -->
      <div class="sync">
        <SyncBadge :status="props.status" />
      </div>

      <header class="head">
        <div class="crown">
          🏆
        </div>
        <h1 v-if="winner">
          {{ nameOf(winner.playerId) }}
        </h1>
        <p
          v-if="winner"
          class="score num"
        >
          {{ winner.total.toLocaleString("de-DE") }}
          <span class="turns">in {{ winner.turns }} Zügen</span>
        </p>
      </header>

      <section
        v-if="awards.length"
        class="awards"
      >
        <div
          v-for="a in awards"
          :key="a.label"
          class="award"
        >
          <div class="award-label">
            {{ a.label }}
          </div>
          <div class="award-who">
            {{ a.who }}
          </div>
          <div class="award-value num">
            {{ a.value }}
          </div>
        </div>
      </section>

      <ol class="standings">
        <li
          v-for="(p, i) in state.standings"
          :key="p.playerId"
          class="row"
        >
          <span class="medal">{{ MEDALS[i] ?? i + 1 }}</span>
          <span class="name">{{ nameOf(p.playerId) }}</span>
          <span class="strikes num">{{ p.struck }}×</span>
          <span class="total num">{{ p.total.toLocaleString("de-DE") }}</span>
        </li>
      </ol>
    </div>

    <div class="foot">
      <button
        class="secondary"
        @click="emit('close')"
      >
        Fertig
      </button>
      <!-- FR-4.4: the same people, the same order, straight away. -->
      <button
        class="primary"
        @click="emit('rematch')"
      >
        Revanche
      </button>
    </div>
  </div>
</template>

<style scoped>
.result {
  height: 100dvh;
  display: flex;
  flex-direction: column;
  padding: calc(var(--safe-top) + 12px) 0 calc(var(--safe-bottom) + 12px);
  padding-left: max(16px, var(--safe-left));
  padding-right: max(16px, var(--safe-right));
}

.scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.sync {
  display: flex;
  justify-content: center;
  padding-top: 4px;
}

.head {
  text-align: center;
  padding: 8px 0 18px;
}

.crown {
  font-size: 40px;
  line-height: 1;
}

.head h1 {
  margin: 6px 0 0;
  font-size: 28px;
  font-weight: 700;
}

.score {
  margin: 2px 0 0;
  font-size: 22px;
  font-weight: 600;
}

.turns {
  display: block;
  font-family: var(--font);
  font-size: 13px;
  font-weight: 400;
  color: var(--text-2);
}

.awards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

.award {
  background: var(--card);
  border-radius: var(--radius-card);
  padding: 10px 8px;
  text-align: center;
  min-width: 0;
}

.award-label {
  font-size: 11px;
  color: var(--text-2);
}

.award-who {
  font-size: 14px;
  font-weight: 600;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.award-value {
  font-size: 13px;
  color: var(--accent);
}

.standings {
  list-style: none;
  margin: 0;
  padding: 0;
}

.row {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--card);
  border-radius: var(--radius-card);
  padding: 9px 12px;
  margin-bottom: 5px;
  min-height: 40px;
}

.medal {
  width: 1.5em;
  text-align: center;
  font-size: 15px;
  color: var(--text-3);
}

.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15px;
}

.strikes {
  font-size: 12px;
  color: var(--text-3);
}

.total {
  font-size: 16px;
  font-weight: 600;
}

.foot {
  display: grid;
  grid-template-columns: 1fr 1.6fr;
  gap: 8px;
  padding-top: 10px;
}

.secondary,
.primary {
  border: none;
  border-radius: var(--radius-control);
  min-height: 50px;
  font-size: 17px;
  font-weight: 600;
}

.secondary {
  background: var(--card);
  color: var(--accent);
}

.primary {
  background: var(--accent);
  color: #fff;
}
</style>
